import React from 'react';
import PropTypes from 'prop-types';
import requiredIf from 'react-required-if';
import ReactSuperSelect from 'react-super-select';
import { utils } from './../helpers';
import {
  uniqWith,
  differenceWith,
  intersectionWith,
  values,
  isEqual,
  flatten
} from 'lodash';
import { isArray } from 'util';
import { SUPER_SELECT_ALL_PREFIX } from './../helpers';

export default class SuperSelectComponent extends React.Component {
  static propTypes = {
    // the options to show in the dropdown as single selectables
    options: PropTypes.arrayOf(PropTypes.string).isRequired,
    // the values expected to be initially selected
    // Expect string or Array of strings only
    // ! - giving an initial value of ['Select All'] will correctly
    // ! - apply all given options as selected on an update
    initialValues: (props, propName) => {
      const initialValues = props[propName];
      const isArray = Array.isArray(initialValues);
      if (isArray && initialValues.includes(v => typeof(v) !== 'string')) {
        return new Error('Invalid prop for multiple selection: ' + propName);
      } else if (!isArray && typeof(initialValues) !== 'string') {
        return new Error('Invalid prop for single selection: ' + propName);
      }
    },
    // displayed when no option has been selected
    noneOption: PropTypes.string,
    // activates the 'Select All' option in the dropdown
    selectAll: PropTypes.bool,
    // the method called with a given option to determine which group it belongs
    getGroup: requiredIf(PropTypes.func, props => props.grouped),
    // activates group selectables in the dropdown
    groupSelect: PropTypes.bool,
    // activates sorting the options by groups
    grouped: requiredIf(PropTypes.bool, props => props.groupSelect),
    // activates multiple selection
    multiple: requiredIf(PropTypes.bool, props =>
      props.selectAll || props.groupSelect || Array.isArray(props.initialValues)),
    // activates custom display that shows number of items selected
    selectedCountDisplay: PropTypes.bool,
    // activates dropdown element filtering
    searchable: PropTypes.bool,
    keepOpenOnSelection: PropTypes.bool,
    onOpenDropdown: PropTypes.func,
    onCloseDropdown: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    // Transforms the default display of options
    // Receives an option, expects return jsx
    customOptionTemplateFunction: PropTypes.func,
    // Called with an option's string to add additional props to an option
    // Expects returned object
    // ! Will not allow changes to { id, name, group } props
    extendOption: PropTypes.func
  };

  availableOptions = [];

  // the defined option to inject into options when the 'selectAll' property is activated
  selectAllOption = {
    id: 'all',
    name: SUPER_SELECT_ALL_PREFIX,
    group: 'Group Select'
  }

  getSelectedSingleOptions = (selectedOptions) => {
    const regex = new RegExp(`^\\${SUPER_SELECT_ALL_PREFIX}`);
    const result = selectedOptions.filter(o => o && !regex.test(o.name));
    return result;
  }

  getSelectedAllOptions = (selectedOptions) => {
    const regex = new RegExp(`^\\${SUPER_SELECT_ALL_PREFIX}`);
    const result = selectedOptions.filter(o => o && regex.test(o.name));
    return result;
  }

  filterOutSelectAllOption = (selectedOptions) =>
    selectedOptions.filter(o => o.id !== this.selectAllOption.id);

  // Attempts to detect a (de)select event of the 'Select All' group option
  // then determines the set of options to return as a result
  updateSelectAll = (selectedOptions, selectionInfo) => {
    const noWorkResult = {
      result: selectedOptions,
      updateComplete: false
    };

    if (!this.props.selectAll) {
      return noWorkResult;
    }

    const {
      possibleAlls,
      selectedAlls,
      selectedSingleOptions,
      relatedOptions,
      lastInSelection
    } = selectionInfo;

    //check if 'Select All' group was selected -> select everything
    const wasAllGroupSelected = lastInSelection.id === this.selectAllOption.id;
    if (wasAllGroupSelected) {
      return {
        result: [...possibleAlls, ...relatedOptions],
        updateComplete: true
      };
    }

    // check if 'Select All' option deselected -> deselect everything
    const allGroupSelected = selectedAlls.some(a => a.id === this.selectAllOption.id);
    const allOtherGroupsSelected = this.filterOutSelectAllOption(selectedAlls).length ===
      this.filterOutSelectAllOption(possibleAlls).length;
    const allSinglesSelected = this.props.options.length === selectedSingleOptions.length;
    if (!allGroupSelected && allOtherGroupsSelected && allSinglesSelected) {
      return {
        result: [],
        updateComplete: true
      };
    }

    // (de)selected option not concerning here -> needs more updating
    return noWorkResult;
  }

  // Attempts to detect a (de)select event of the a group option
  // other than the 'Select All and then determines the set
  // of options to return as a result
  updateGroup = (selectedOptions, selectionInfo) => {
    const noWorkResult = {
      result: selectedOptions,
      updateComplete: false
    };

    if (!this.props.groupSelect) {
      return noWorkResult;
    }

    const {
      possibleAlls,
      selectedAlls,
      selectedSingleOptions,
      relatedOptions,
      lastInSelection,
      groupId
    } = selectionInfo;

    // can assume if the group option is last then it was just selected
    const wasGroupSelected = lastInSelection.id === groupId;
    if (wasGroupSelected) {
      // group selected => select all related singles for this group
      return {
        result: uniqWith(
          [...selectedAlls, ...selectedSingleOptions.concat(relatedOptions)],
          isEqual
        ),
        updateComplete: true
      };
    }

    let resultAlls = selectedAlls;
    let resultSingles = selectedSingleOptions;
    let updateComplete = false;

    // if Group was possibly just deselected, remove the 'Select All' anyway
    // ! - Cannot know for sure so must continue on
    if (selectedAlls.length < possibleAlls.length) {
      resultAlls = this.filterOutSelectAllOption(resultAlls);
    }

    // determine if a related single was deselected -> deselect relevant group
    const relatedSelectedSinglesLength =
        intersectionWith(relatedOptions, selectedSingleOptions, isEqual).length;
    const isPartialSet = relatedSelectedSinglesLength !== relatedOptions.length;
    const isPartialSetOffBy1 =
        isPartialSet && relatedSelectedSinglesLength === relatedOptions.length - 1;

    const groupInSelection = resultAlls.some(a => a.id === groupId);
    if (groupInSelection && isPartialSetOffBy1) {
      // single node deselected => deselect related group nodes
      resultAlls = resultAlls.filter(o => o.id !== groupId);
      updateComplete = true;
    }
    if (!groupInSelection && !isPartialSet) {
      // group node deselected => deselect all related singles
      resultSingles = differenceWith(selectedSingleOptions, relatedOptions, isEqual);
      updateComplete = true;
    }

    return {
      result: uniqWith(
        [...resultAlls, ...resultSingles],
        isEqual
      ),
      updateComplete
    };
  }

  // given a set of selected options, this decides whether a select or deselect
  // occured based on observational assumptions and updates the selection
  // by applying or removing options if a group was clicked
  updateOptions = (selectedOptions) => {
    if (selectedOptions.length === 0) { return []; }
    if (!(this.props.multiple && (this.props.selectAll || this.props.groupSelect))) {
      return selectedOptions;
    }

    const optionsByGroups = this.getOptionsByGroup();
    const selectionInfo = {
      possibleAlls: this.getGroupOptions(this.availableOptions),
      selectedAlls: this.getSelectedAllOptions(selectedOptions),
      selectedSingleOptions: this.getSelectedSingleOptions(selectedOptions),
      lastInSelection: selectedOptions[selectedOptions.length - 1]
    };

    const processedSelectAll = this.updateSelectAll(
      selectedOptions,
      {
        ...selectionInfo,
        relatedOptions: optionsByGroups[this.selectAllOption.id],
        groupId: this.selectAllOption.id
      }
    );
    if (processedSelectAll.updateComplete) {
      return processedSelectAll.result;
    }

    let updateResult = processedSelectAll;
    // check for required update for every group that is not the 'Select All' group
    this.filterOutSelectAllOption(selectionInfo.possibleAlls)
      .forEach(groupOption => {
        if (updateResult.updateComplete) {
          return;
        }

        updateResult = this.updateGroup(
          updateResult.result,
          {
            ...selectionInfo,
            groupId: groupOption.id,
            relatedOptions: optionsByGroups[groupOption.id]
          }
        );
      });

    // final checks and cleanup
    let result = uniqWith(updateResult.result, isEqual);

    // check if a single was deselected -> deselect 'Select All'
    const selectedSingles = this.getSelectedSingleOptions(result);
    if (selectedSingles.length < this.props.options.length) {
      result = this.filterOutSelectAllOption(result);
    }

    return result;
  }

  // given a set of options to display, this will expand the set to include
  // various group options if correct props indicate to do so
  cleanInitialOptions = (options) => {
    if (!this.props.multiple) {
      // if single then return given
      return options;
    }

    if (options.length === 0) { return []; }
    if (!(this.props.multiple && (this.props.selectAll || this.props.groupSelect))) {
      return options;
    }

    const relatedOptions = this.getOptionsByGroup();
    //find selected groups from state
    const selectedGroups =
      options.filter(o => o.name.startsWith(SUPER_SELECT_ALL_PREFIX));
    //find the nodes that the selected groups represent
    const nodesFromGroups =
      flatten(selectedGroups.map(o => o.name === SUPER_SELECT_ALL_PREFIX ?
        relatedOptions['all'] :
        relatedOptions[o.name.slice(SUPER_SELECT_ALL_PREFIX.length + 1)]));

    //find selected singles from state
    // possible that some options from a previous availableOptions set leaked
    // over due to onChange with 'Select All' on, if so remove them also
    const singlesLookup = relatedOptions['all'].reduce((r, o) => {
      return { ...r, [o.name]: o };
    }, {});
    const singles = options.filter(o =>
      !o.name.startsWith(SUPER_SELECT_ALL_PREFIX) && singlesLookup[o.name]
    );

    const together = uniqWith(nodesFromGroups.concat(singles), isEqual);
    const possibleGroups = this.getGroupOptions(this.availableOptions);
    const groupOptions = selectedGroups.some(g => g.name === SUPER_SELECT_ALL_PREFIX) ?
      possibleGroups :
      possibleGroups.filter(possibleGroup =>
        selectedGroups.some(selectedGroup => isEqual(possibleGroup, selectedGroup)));
    return groupOptions.concat(together);
  };

  convertSingleToOption = (name) => {
    console.assert(!Array.isArray(name));
    const singleOption = name ?
      this.makeOption(name) :
      undefined;
    return singleOption;
  }

  convertMultipleToOptions = (nameArr) => {
    console.assert(Array.isArray(nameArr));
    if (nameArr.length === 1 && nameArr[0] === 'Select All') {
      return [this.selectAllOption];
    }
    const multipleOptions = nameArr.map(s => this.makeOption(s));
    return multipleOptions;
  }

  /**
   * Converts a string into an option object for use with SuperSelect
   * @param {string} name - the string to display as the option
   */
  makeOption = (name) => {
    console.assert(typeof(name) === 'string');

    const extension = {};
    if (this.props.extendOption) {
      const givenExtension = this.props.extendOption(name);
      Object.assign(extension, givenExtension);
    }

    const id = name.startsWith(SUPER_SELECT_ALL_PREFIX) ?
      name.slice(SUPER_SELECT_ALL_PREFIX.length + 1) :
      name;
    const group = this.props.getGroup ?
      { group: this.superGetGroup(name) } :
      {};
    return {
      ...extension,
      id,
      name,
      ...group
    };
  }

  // only calls provided getGroup if option is not an applied group option
  superGetGroup = (option) => option.startsWith(SUPER_SELECT_ALL_PREFIX) ?
    'Group Select' :
    this.props.getGroup(option);

  // returns a mapping of all the options by group
  getOptionsByGroup = () => {
    const byGroups = this.props.grouped ?
      utils.propertyAccumulatorReducerByFunc(
        this.availableOptions,
        o => this.superGetGroup(o.name)
      ) :
      {};
    const byAll = this.props.selectAll || this.props.grouped ?
      { all: this.availableOptions } :
      {};
    return {
      ...byGroups,
      ...byAll
    };
  };

  // returns all possible groups as options for a Select
  // based on the given options
  getGroupOptions = (options) => {
    if (!this.props.multiple) { return []; }
    const allOption = this.props.selectAll ? [this.selectAllOption] : [];

    const groupOptions = this.props.groupSelect ?
      values(options.reduce((result, option) => {
        result[option.group] = {
          id: option.group,
          name: `${SUPER_SELECT_ALL_PREFIX} ${option.group}`,
          group: 'Group Select'
        };
        return result;
      }, {})) :
      [];

    return [
      ...allOption,
      ...groupOptions,
    ];
  };

  render() {
    const {
      customOptionTemplateFunction,
      grouped,
      groupSelect,
      initialValues,
      multiple,
      noneOption,
      onChange,
      options,
      selectedCountDisplay,
      searchable,
      onOpenDropdown,
      onCloseDropdown,
      keepOpenOnSelection
    } = this.props;

    // convert both to options
    this.availableOptions = this.convertMultipleToOptions(options);
    const initialOptions = multiple ?
      this.convertMultipleToOptions(initialValues) :
      this.convertSingleToOption(initialValues);

    // initialValues may be string not array => cleaned may also be string
    // * ReactSuperSelect below will accept either type as initialValue
    const cleanedInitialValues = this.cleanInitialOptions(initialOptions);

    const dataSource = [
      ...this.getGroupOptions(this.availableOptions),
      ...this.availableOptions
    ];

    const customBuiltInCountDisplay = (selection) => {
      return (<div>{`${selection.length} item(s) selected.`}</div>);
    };

    return (
      <ReactSuperSelect
        initialValue={cleanedInitialValues}
        dataSource={dataSource}
        placeholder={noneOption}
        onChange={(selected) => {
          if (!multiple) {
            // strict return of undefined if falsy
            const selectedName = selected ? selected.name : undefined;
            onChange(selectedName);
            return;
          }

          console.assert(multiple);
          if (!selected) {
            selected = [];
          }
          const { initialValues } = this.props;

          const selectedOptions = (isArray(selected) ? selected : [selected])
            .filter(a => a);

          let resultOptions = selectedOptions;
          // Group selection forces two onChanges in the UI, one for the group clicked
          // and one for the related options being applied through updateOptions.
          // ! Considering that, we should ignore an updateOption call on the second
          // ! onChange since nothing else would have been clicked by the user and
          // ! we don't want to treat the second onChange as such
          const selectedValues = selectedOptions.map(o => o.name);
          const stateAndSelectedAreSame =
            selectedValues.length === initialValues.length &&
            differenceWith(
              selectedValues,
              initialValues,
              isEqual
            ).length === 0;

          // call updateOptions to apply any side effects to options set
          resultOptions = stateAndSelectedAreSame ?
            selectedOptions :
            this.updateOptions(selectedOptions);

          onChange(resultOptions.map(o => o.name));
        }}
        groupSelect={groupSelect}
        groupBy={grouped ? 'group' : undefined}
        multiple={multiple}
        searchable={searchable}
        onOpenDropdown={onOpenDropdown}
        onCloseDropdown={onCloseDropdown}
        keepOpenOnSelection={keepOpenOnSelection}
        customSelectedValueTemplateFunction={
          selectedCountDisplay && customBuiltInCountDisplay
        }
        customOptionTemplateFunction={customOptionTemplateFunction}
      />
    );
  }
}


// WEBPACK FOOTER //
// src/Components/SuperSelectComponent.jsx